import React, { useState, useEffect } from 'react';
import './Scheduler.css';
import LocationSelection from './LocationSelection';
import ServiceSelection from './ServiceSelection';
import DateSelection from './DateSelection';
import PersonalInfoForm from './PersonalInfoForm';
import PhoneValidationStep from './PhoneValidationStep';
import BookingRecap from './BookingRecap';
import StepIndicator from './StepIndicator';
import SuccessScreen from './SuccessScreen';
import CancelledScreen from './CancelledScreen';
import PoweredByCinch from './PoweredByCinch';
import { submitBooking } from '../utils/api';
import { useBranding } from '../contexts/BrandingContext';
import { useCalendar } from '../contexts/CalendarContext';
import { useSession } from '../contexts/SessionContext';
import { useLeadSalvage } from '../contexts/LeadSalvageContext';
import { updateSession } from '../utils/api';
import { deleteCookie } from '../utils/cookies';
import {
  getContactFields,
  getExtraFields,
  getPossessionModel,
  getPossessionFields,
  fieldStateKey,
  isFieldRequired,
} from '../utils/dataFields';
// CIN-6879: Reserve with Google requires the store address above the fold on
// EVERY landing page. This header is the only one the widget renders (two JSX
// copies: the cancelled/expired screen and the wizard, which also covers the
// step-6 success screen), so rendering the address here covers every step.
import { formatAddressSingleLine } from '../utils/address';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';

// Styled progress bar component
const BorderLinearProgress = styled(LinearProgress)(({ theme, primarycolor }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#e5e7eb', // light gray
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: primarycolor || '#1a90ff',
  },
}));

const Scheduler = () => {
  const { branding, widgetConfig } = useBranding();
  const { calendarId, locations, validatePhone, locationIdParam, fields } = useCalendar();
  // CIN-6274: route real close entry points (X button + overlay click) through
  // the lead-salvage gate. closeWidget() stays available on useCalendar() as the
  // raw close, which the salvage flow itself invokes after show/dismiss/submit.
  const { attemptClose } = useLeadSalvage();
  const {
    sessionId,
    currentStep,
    setCurrentStep,
    selectedLocation,
    selectedServices,
    selectedTimeSlot,
    personalInfo,
    phoneVerified,
    selectedCustomerId,
    selectedVehicleId,
    vehicleCustomerRefId,
    vehiclePossessionId,
    cancelled,
    schedule,
    setSchedule,
  } = useSession();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // CIN-6879: single-line store address for the modal header. Empty string when
  // no location is resolved yet (multi-location step 1, where the list itself
  // shows every address) or when the location carries no address fields, so the
  // node is simply absent rather than rendered blank.
  const locationAddress = formatAddressSingleLine(selectedLocation);
  const [bookingError, setBookingError] = useState(null);

  const handleBookingSubmit = async (bookingData) => {
    return await submitBooking(calendarId, {
      session_id: sessionId,
      ...bookingData
    });
  };

  const totalSteps = 5;
  const stepTitles = {
    1: "Location",
    2: "Services",
    3: "Date & Time",
    4: "Personal Information",
    5: "Review & Confirm",
    6: "Success"
  };


  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Skip location step if there's only one location or locationId was provided
      if (currentStep === 2 && (locations.length === 1 || !!locationIdParam)) {
        // Don't go back to step 1
        return;
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGoToStep = (stepNumber) => {
    // Prevent going to step 1 if there's only one location or locationId was provided
    if (stepNumber === 1 && (locations.length === 1 || !!locationIdParam)) {
      return;
    }
    setCurrentStep(stepNumber);
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return selectedLocation !== null;
      case 2:
        return selectedServices.length > 0;
      case 3:
        return selectedTimeSlot !== null;
      case 4: {
        // If phone validation is required and phone is not verified yet
        if (validatePhone && !phoneVerified) {
          // Only need phone number to be entered
          const digits = personalInfo.phone ? personalInfo.phone.replace(/[^\d]/g, '') : '';
          return digits.length === 10 || digits.length === 11; // allow leading 1 for US country code
        }
        const hasValue = (key) => personalInfo[key] && String(personalInfo[key]).trim() !== '';
        // When an existing customer / possession is chosen, its section is hidden and the
        // record is sent by id — so those fields aren't required (mirrors the backend, which
        // only requires contact fields when no customer_id/customer_ref_id is provided).
        const contactShown = !selectedCustomerId || selectedCustomerId === 'new';
        const possessionIsExisting = selectedVehicleId && selectedVehicleId !== 'new';
        const configuredValid = [
          ...(contactShown ? getContactFields(fields) : []),
          ...(possessionIsExisting ? [] : getPossessionFields(fields)),
          ...getExtraFields(fields),
        ].every((field) => {
          if (!isFieldRequired(field)) return true;
          if (field.id === 'phone') {
            const digits = personalInfo.phone ? personalInfo.phone.replace(/[^\d]/g, '') : '';
            return digits.length === 10 || digits.length === 11; // allow leading 1 for US country code
          }
          return hasValue(fieldStateKey(field));
        });
        // CIN-6385: email is optional, but if the customer typed one it must be a valid
        // format — otherwise the /book serializer 400s at Confirm. Gate Next until email
        // is blank or well-formed.
        const trimmedEmail = (personalInfo.email || '').trim();
        const emailValid = trimmedEmail === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
        return configuredValid && emailValid;
      }
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      // CIN-6274: gate the close so an abandoning consumer sees the salvage modal.
      attemptClose();
    }
  };

  const renderProgressIndicator = () => {
    const progressType = widgetConfig?.progress_indicator || 'stepper';

    if (progressType === 'none') {
      return null;
    }

    const progress = (currentStep / totalSteps) * 100;

    switch (progressType) {
      case 'bar':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 16px' }}>
            <BorderLinearProgress
              variant="determinate"
              value={progress}
              primarycolor={branding.primary_color}
              sx={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '40px' }}>
              {Math.round(progress)}%
            </span>
          </div>
        );
      case 'line':
        return (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              backgroundColor: '#e5e7eb', // light gray
              '& .MuiLinearProgress-bar': {
                backgroundColor: branding.primary_color
              }
            }}
          />
        );
      case 'stepper':
      default:
        return (
          <StepIndicator
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
        );
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <LocationSelection />;
      case 2:
        return <ServiceSelection />;
      case 3:
        return <DateSelection onChangeServices={handleBack} />;
      case 4:
        // Show phone validation if validatePhone is true and phone not verified
        if (validatePhone && !phoneVerified) {
          return <PhoneValidationStep />;
        }
        // Otherwise show the full personal info form
        return <PersonalInfoForm />;
      case 5:
        return <BookingRecap />;
      case 6:
        return <SuccessScreen />;
      default:
        return null;
    }
  };

  // If the appointment is cancelled, show the cancelled screen
  if (cancelled) {
    // Check if the appointment is expired
    const isExpired = schedule?.appointment_naive && new Date(schedule.appointment_naive) < new Date();

    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal-content">
          <div className="modal-header">
            {branding?.logo_url && (
              <div className="modal-logo">
                <img src={branding.logo_url} alt="Company Logo" />
              </div>
            )}
            <div className="modal-title-group">
              <h2>Appointment {isExpired ? 'Expired' : 'Cancelled'}</h2>
              {/* CIN-6784: merchant name, matching the Reserve with Google entity feed. */}
              {branding?.company_name && (
                <div className="modal-company-name" data-testid="modal-company-name">
                  {branding.company_name}
                </div>
              )}
              {/* CIN-6879: store address, directly under the merchant name. */}
              {locationAddress && (
                <div className="modal-location-address" data-testid="modal-location-address">
                  {locationAddress}
                </div>
              )}
            </div>
            <button className="close-button" onClick={attemptClose}>×</button>
          </div>
          <div className="modal-body">
            <CancelledScreen />
          </div>
          <PoweredByCinch />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          {branding?.logo_url && (
            <div className="modal-logo">
              <img src={branding.logo_url} alt="Company Logo" />
            </div>
          )}
          <div className="modal-title-group">
            <h2>{
              currentStep === 4 && validatePhone && !phoneVerified
                ? "Phone Verification"
                : stepTitles[currentStep]
            }</h2>
            {/* CIN-6784: merchant name, matching the Reserve with Google entity feed. */}
            {branding?.company_name && (
              <div className="modal-company-name" data-testid="modal-company-name">
                {branding.company_name}
              </div>
            )}
            {/* CIN-6879: store address, directly under the merchant name. */}
            {locationAddress && (
              <div className="modal-location-address" data-testid="modal-location-address">
                {locationAddress}
              </div>
            )}
          </div>
          <button className="close-button" onClick={attemptClose}>×</button>
        </div>

        {currentStep <= totalSteps && renderProgressIndicator()}

        <div className="modal-body">
          {renderStep()}
          {/* CIN-6620: the confirm-step block message (e.g. the vehicle
              double-booking 409) is rendered here verbatim from the API body. */}
          {bookingError && currentStep === 5 && (
            <div data-testid="booking-error" style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c00',
              textAlign: 'center'
            }}>
              {bookingError}
            </div>
          )}
        </div>

        {currentStep <= totalSteps && (
          <div className="modal-footer">
            <div className="modal-actions" style={{
              justifyContent: (currentStep === 1 || (currentStep === 2 && (locations.length === 1 || !!locationIdParam))) ? 'flex-end' : 'space-between'
            }}>
              {currentStep > 1 && !(currentStep === 2 && (locations.length === 1 || !!locationIdParam)) && (
                <button
                  className="action-button secondary"
                  onClick={handleBack}
                >
                  Back
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  className="action-button primary"
                  onClick={handleNext}
                  disabled={!isStepValid()}
                >
                  Next
                </button>
              ) : (
                <button
                  className="action-button primary"
                  onClick={async () => {
                    setIsSubmitting(true);
                    setBookingError(null);

                    const possessionModel = getPossessionModel(fields);
                    const isVehicle = possessionModel === 'vehicle';
                    // Possession field values keyed by field id (used for animal / real_estate).
                    const possessionValues = getPossessionFields(fields).reduce((acc, field) => {
                      const key = fieldStateKey(field);
                      acc[key] = personalInfo[key] || null;
                      return acc;
                    }, {});
                    // Generic label + summary for the booked possession (recap / iCal / postMessage).
                    const possessionLabel = fields?.possession_label || 'Details';
                    const possessionSummary = getPossessionFields(fields)
                      .map((field) => personalInfo[fieldStateKey(field)])
                      .filter(Boolean)
                      .join(' ');

                    const bookingData = {
                      calendar_location_id: selectedLocation.id,
                      service_ids: selectedServices.map(s => s.id),
                      date: selectedTimeSlot, // Full ISO datetime string

                      // Service details for backend
                      type: selectedServices.map(s => s.name).join(', '),
                      description: selectedServices.map(s => s.description).filter(d => d).join('; '),

                      first_name: personalInfo.first_name,
                      last_name: personalInfo.last_name,
                      phone: personalInfo.phone,
                      // CIN-6385: the /book serializer's email field is
                      // EmailField(required=False, allow_blank=True) with NO
                      // allow_null, so a blank email MUST be sent as "" (never
                      // null, which 400s). Trim so whitespace-only isn't sent
                      // as an invalid non-blank value.
                      email: (personalInfo.email || '').trim(),

                      // Possession: vehicle uses the dedicated vehicle_* fields + Motor lookups;
                      // animal / real_estate send a generic possession dict keyed by field id.
                      possession_model: possessionModel || null,
                      ...(isVehicle
                        ? {
                            vehicle_year: personalInfo.year,
                            vehicle_make: personalInfo.make,
                            vehicle_model: personalInfo.model,
                          }
                        : { possession: possessionValues }),

                      // Include selected customer and possession IDs
                      customer_id: selectedCustomerId !== '' && selectedCustomerId !== 'new' ? selectedCustomerId : null,
                      vehicle_id: isVehicle && selectedVehicleId && selectedVehicleId !== 'new' ? selectedVehicleId : null,
                      customer_ref_id: vehicleCustomerRefId !== '' && vehicleCustomerRefId !== 'new' ? vehicleCustomerRefId : null,
                      possession_id: vehiclePossessionId !== '' && vehiclePossessionId !== 'new' ? vehiclePossessionId : null,

                      // Include comments if provided
                      comments: personalInfo.comments || null,

                      // All configured extra data fields, stored on the schedule's entity_data
                      entity_data: (fields?.extra || []).reduce((acc, field) => {
                        const key = field.id.replace(/^entity_data__/, '');
                        acc[key] = personalInfo[key] || null;
                        return acc;
                      }, {}),
                    }

                    const result = await handleBookingSubmit(bookingData);
                    setIsSubmitting(false);

                    if (result.status === 429) {
                      setBookingError('You are submitting bookings too quickly. Please wait a moment and try again.');
                    } else if (result.success) {
                      // Mark session as complete
                      await updateSession(calendarId, sessionId, { schedule: result?.data?.schedule_id });
                      
                      // CIN-5926: drop the persistent session cookie now that
                      // a schedule is booked against this session. The local
                      // sessionId state remains so Edit/Cancel work in this
                      // tab; a future page load will start a fresh session
                      // instead of silently re-attaching to a finalized one.
                      deleteCookie(`scheduler_session_${calendarId}`);
                      setSchedule({
                        id: result?.data?.id,
                        appointment_naive: selectedTimeSlot,
                        type: selectedServices.map(s => s.name).join(', '),
                        location_name: result?.data?.location_name,
                        possession_label: possessionLabel,
                        possession_summary: possessionSummary,
                        customer_first_name: personalInfo.first_name,
                        customer_last_name: personalInfo.last_name,
                      });
                      
                      const bookingDetails = {
                        id: result?.data?.id,
                        schedule_id: result?.data?.schedule_id,
                        appointment_date: selectedTimeSlot,
                        services: selectedServices.map(s => ({ id: s.id, name: s.name, description: s.description })),
                        location: {
                          id: selectedLocation.id,
                          name: result?.data?.location_name
                        },
                        customer: {
                          first_name: personalInfo.first_name,
                          last_name: personalInfo.last_name,
                          phone: personalInfo.phone,
                          email: personalInfo.email
                        },
                        possession: {
                          model: possessionModel || null,
                          label: possessionLabel,
                          summary: possessionSummary,
                          values: possessionValues
                        },
                        comments: personalInfo.comments
                      };

                      // Notify parent window of booking completion
                      window.parent.postMessage({
                        type: 'BOOKING_COMPLETED',
                        data: bookingDetails
                      }, '*');

                      // Redirect to success step
                      setCurrentStep(6);
                    } else {
                      // CIN-6620: prefer the server's own message (the vehicle
                      // double-booking guard returns 409 with the block copy
                      // and the shop's phone number) so the customer reads the
                      // backend string verbatim, with no client-side rewording.
                      // Deliberately does NOT advance the step: no appointment
                      // was created, so the customer stays on the recap and is
                      // never shown the success screen.
                      setBookingError(result.data?.message || result.error);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                </button>
              )}
            </div>
          </div>
        )}

        <PoweredByCinch />
      </div>
    </div>
  );
};

export default Scheduler;